import { Button, Form, Input, Popconfirm, Select, Table } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { toast } from 'react-toastify';

import { deleteLeavePolicy, getAllLeaveAllocation, leaveAllocationAllUsers, updateLeavePolicy, fetchValidityEntry, getDefaultValidity, getBulkUploadSampleData } from '../../redux/rtk/features/leavePolicy/leavePolicySlice';
import getCompanyIdFromToken from '../../utils/getCompanyIdFromToken';
import { StyledExcelButton } from '../../utils/excelExport';
import EditLeavePolicyModal from './editLeavePolicy';
import AllocatedLeaveModal from '../UI/PopUp/AllocatedLeaveModal';


const AllocatedLeave = () => {

    const { allocatedLeave = [], validity, defaultValidity, loading, bulkUploadSampleData } = useSelector((state) => state.leavePolicy);

    const [loader, setLoader] = useState(false);
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [selectedPolicy, setSelectedPolicy] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedValidityId, setSelectedValidityId] = useState(null);
    const companyId = getCompanyIdFromToken();
    const [selectedLeaveTypes, setSelectedLeaveTypes] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedLeaveData, setSelectedLeaveData] = useState(null);
    
    useEffect(() => {
        dispatch(getAllLeaveAllocation());
        dispatch(fetchValidityEntry());
        dispatch(getDefaultValidity());
    }, [dispatch]);

    // Set default validity when data is loaded
    useEffect(() => {
        if (defaultValidity && defaultValidity.length > 0) {
            setSelectedValidityId(defaultValidity[0]?.validity_id);
        }
    }, [defaultValidity]);

    // Get unique leave types based on selected validity
    const getLeaveTypeOptions = () => {
        if (!selectedValidityId) return [];
        
        const filteredByValidity = allocatedLeave.filter(item => 
            item.leavePolicy?.validity_id === selectedValidityId
        );
        
        const uniqueLeaveTypes = Array.from(new Set(
            filteredByValidity.map(item => item.leavePolicy?.leaveType?.leaveType)
        ))
        .filter(Boolean) // Remove any falsy values
        .sort((a, b) => a.localeCompare(b)) // Alphabetical sort
        .map(leaveType => ({
            label: leaveType,
            value: leaveType,
        }));
        
        return uniqueLeaveTypes;
    };

    const leaveTypeOptions = getLeaveTypeOptions();
    const handleDelete = (id) => {
        dispatch(deleteLeavePolicy(id))
            .then(() => {
                dispatch(getAllLeaveAllocation());
            })
            .catch((error) => {
                toast.error('Error in deleting leave policy');
            });
    };

    const addKeys = (arr) => arr.map((i, index) => ({ ...i, key: i.id, serial: index + 1 }));

    const handleEditClick = (record) => {
        setSelectedPolicy(record);
        setEditModalVisible(true);
    };

    const handleEditModalCancel = () => {
        setEditModalVisible(false);
        setSelectedPolicy(null);
    };

    const handleEditModalOk = (values) => {
        dispatch(updateLeavePolicy({ id: selectedPolicy.id, values }))
            .then(() => {
                setEditModalVisible(false);
                setSelectedPolicy(null);
                dispatch(getAllLeaveAllocation());
            })
            .catch((error) => {
                toast.error("Error updating policy");
            });
    };

    const handleLeaveAllocationClick = () => {
        setLoader(true);
        dispatch(leaveAllocationAllUsers({ company_id: companyId }))
            .then(() => {
                setLoader(false);
                dispatch(getAllLeaveAllocation());
            })
            .catch((error) => {
                setLoader(false);
                toast.error('Error in adding Leave Allocations. Try again.');
                console.log(error.message);
            });
    };

    const filteredList = allocatedLeave.filter(item => {
        const leaveTypeName = item.leavePolicy?.leaveType?.leaveType || '';
        const firstName = item.user?.firstName || '';
        const lastName = item.user?.lastName || '';
        const employeeId = item.user?.employeeId || '';

        // Combined search logic for both employee name and employee code
        const searchLower = searchTerm.toLowerCase();
        
        // Filter by validity_id if provided
        const validityMatch = selectedValidityId ? item.leavePolicy?.validity_id === selectedValidityId : true;
        
        // Filter by leave types if provided
        const leaveTypeMatch = selectedLeaveTypes.length === 0 || selectedLeaveTypes.includes(leaveTypeName);
        
        return (
            validityMatch &&
            leaveTypeMatch &&
            (`${firstName} ${lastName}`.toLowerCase().includes(searchLower) || employeeId.toLowerCase().includes(searchLower))
        );
    });


    const sortedList = filteredList.sort((a, b) => {
        const nameA = `${a.user?.firstName || ''} ${a.user?.lastName || ''}`.toLowerCase();
        const nameB = `${b.user?.firstName || ''} ${b.user?.lastName || ''}`.toLowerCase();

        if (nameA < nameB) {
            return -1; // a comes before b
        }
        if (nameA > nameB) {
            return 1; // b comes before a
        }
        return 0; // names are equal
    });

    // CSV data for current allocation report (Download Excel button)
    const csvData = sortedList.map((item, index) => {
        const dataPoint = {
            serial: index + 1,
            employeeId: item.user?.employeeId || '',
            employeeName: `${item.user?.firstName || ''} ${item.user?.lastName || ''}`.trim(),
            leaveType: item.leavePolicy?.leaveType?.leaveType || '',
            annualLeave: item.annualLeave !== null && item.annualLeave !== undefined ? Number(item.annualLeave) : 0,
            pendingLeaves: item.pendingLeaves !== null && item.pendingLeaves !== undefined ? Number(item.pendingLeaves) : 0,
        };
        
        return dataPoint;
    });

    // Headers for allocation report Excel
    const headers = [
        { label: "S.No", key: "serial" },
        { label: "Employee ID", key: "employeeId" },
        { label: "Employee Name", key: "employeeName" },
        { label: "Leave Type", key: "leaveType" },
        { label: "Total Leave", key: "annualLeave" },
        { label: "Pending Leaves", key: "pendingLeaves" },
    ];

    const columns = [
        {
            title: 'S.No',
            dataIndex: 'serial',
            key: 'serial',
        },
        {
            title: 'Employee Name',
            key: 'employeeName',
            // render: (_, record) => `${record.user.firstName} ${record.user.lastName}`,
            render: (_, record) => `${record.user?.firstName || ''} ${record.user?.lastName || ''}`.trim() || 'N/A',
        },
        {
            title: 'Employee Code',
            key: 'employeeCode',
            //   render: (_, record) => `${record.user.employeeId}`,
            render: (_, record) => record.user.employeeId || 'N/A',

        },
        {
            title: 'Leave Type',
            key: 'leaveType',
            render: (_, record) => record.leavePolicy?.leaveType?.leaveType || 'N/A',
        },
        {
            title: 'Total Leave',
            dataIndex: 'annualLeave',
            key: 'annualLeave',
        },
        // {
        //     title: 'Monthly Leave',
        //     dataIndex: 'monthlyLeave',
        //     key: 'monthlyLeave',
        // },
        {
            title: 'Pending Leaves',
            dataIndex: 'pendingLeaves',
            key: 'pendingLeaves',
        },
    ];

    const handleLeaveTypeChange = (value) => {
        setSelectedLeaveTypes(value);
    };

    const handleValidityChange = (validityId) => {
        setSelectedValidityId(validityId);
        setSelectedLeaveTypes([]); // Reset leave types when validity changes
    };

    // Filter validity data to show only entries where applyFor is true
    const filteredValidity = validity ? validity.filter(item => item.applyFor === true) : [];

    const handleOpenModal = async (record) => {
        console.log('🔵 handleOpenModal called', { selectedValidityId });
        
        if (!selectedValidityId) {
            toast.warning('Please select a validity period first');
            return;
        }
        
        setSelectedLeaveData(record);
        setLoader(true);
        
        try {
            console.log('🔵 Fetching fresh allocation data...');
            // First refresh the allocation data to get latest values from database
            const allocationResult = await dispatch(getAllLeaveAllocation()).unwrap();
            console.log('✅ Allocation data fetched:', allocationResult?.length, 'records');
            
            console.log('🔵 Fetching bulk upload sample data for validity:', selectedValidityId);
            // Then fetch fresh sample data with latest values
            const sampleResult = await dispatch(getBulkUploadSampleData(selectedValidityId)).unwrap();
            console.log('✅ Sample data fetched:', sampleResult?.length, 'records');
            
            // Now open the modal with fresh data
            setIsModalOpen(true);
        } catch (error) {
            toast.error('Error loading data. Please try again.');
            console.error('❌ Error loading modal data:', error);
        } finally {
            setLoader(false);
        }
    };

    const handleCloseModal = async () => {
        console.log('🔵 handleCloseModal called');
        setIsModalOpen(false);
        setSelectedLeaveData(null);
        // Refresh data when modal closes to ensure latest values are shown
        console.log('🔵 Refreshing allocation data after modal close...');
        const result = await dispatch(getAllLeaveAllocation()).unwrap();
        console.log('✅ Allocation data refreshed:', result?.length, 'records');
    };

    // Get selected validity name for CSV filename
    const getSelectedValidityName = () => {
        if (!selectedValidityId || !validity || validity.length === 0) {
            return 'AllValidities';
        }
        const selectedValidity = validity.find(v => v.id === selectedValidityId);
        return selectedValidity ? selectedValidity.name.replace(/\s+/g, '_') : 'Unknown';
    };

    // Get current date for filename
    const getCurrentDate = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const csvFileName = `Leave_Allocation_Report_${getSelectedValidityName()}_${getCurrentDate()}.csv`;

    // Prepare sample CSV data for bulk upload template
    const getSampleCsvData = () => {
        if (!bulkUploadSampleData || bulkUploadSampleData.length === 0) {
            return [];
        }
        return bulkUploadSampleData.map((item, index) => ({
            serial: index + 1,
            employeeId: item.employeeId || '',
            employeeName: item.employeeName || '',
            leaveType: item.leaveType || '',
            annualLeave: item.annualLeave !== null && item.annualLeave !== undefined ? Number(item.annualLeave) : 0,
        }));
    };

    const sampleCsvHeaders = [
        { label: "S.No", key: "serial" },
        { label: "Employee ID", key: "employeeId" },
        { label: "Employee Name", key: "employeeName" },
        { label: "Leave Type", key: "leaveType" },
        { label: "Total Leave", key: "annualLeave" },
    ];

    const sampleFileName = `Bulk_Upload_Template_${getSelectedValidityName()}_${getCurrentDate()}.csv`;

    return (
        <>


            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                
                <div style={{ display: 'flex', gap: '8px' }}>
                    <StyledExcelButton
                        data={csvData}
                        headers={headers}
                        filename={csvFileName.replace('.csv', '')}
                        buttonText="Download Excel"
                        options={{
                            headerColor: '1890ff',
                            sheetName: 'Leave Allocation',
                            title: `Leave Allocation Report - ${getSelectedValidityName()}`
                        }}
                    />
                    <Button 
                        type="primary" 
                        onClick={() => handleOpenModal(null)}
                        disabled={!selectedValidityId}
                    >
                        Upload
                    </Button>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Select
                        placeholder="Select Validity Policy"
                        value={selectedValidityId}
                        onChange={handleValidityChange}
                        style={{ width: '200px' }}
                        loading={loading}
                    >
                        {filteredValidity && filteredValidity.length > 0 ? (
                            filteredValidity.map((validityItem) => (
                                <Select.Option key={validityItem.id} value={validityItem.id}>
                                    {validityItem.name}
                                </Select.Option>
                            ))
                        ) : (
                            <Select.Option disabled>No validity policies available</Select.Option>
                        )}
                    </Select>
                    <Select
                        mode="multiple"
                        placeholder="Select Leave Types"
                        options={leaveTypeOptions}
                        value={selectedLeaveTypes}
                        onChange={handleLeaveTypeChange}
                        style={{ width: '200px' }}
                        disabled={!selectedValidityId}
                    />
                    <Input
                        placeholder="Search by Name or Employee Code"
                        style={{ width: '300px' }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <Table columns={columns} dataSource={sortedList ? addKeys(sortedList) : []} />

            <EditLeavePolicyModal
                visible={editModalVisible}
                onCancel={handleEditModalCancel}
                onOk={handleEditModalOk}
                initialValues={selectedPolicy}
                leaveTypes={leaveTypes}
                loading={loading}
            />

            <AllocatedLeaveModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                selectedData={selectedLeaveData}
                csvData={getSampleCsvData()}
                csvHeaders={sampleCsvHeaders}
                csvFileName={sampleFileName}
                selectedValidityId={selectedValidityId}
            />
        </>
    );
};

export default AllocatedLeave;
